import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { type ColDef, type TableRef } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import { MyDecisionsGroups } from "./MyDecisionsGroups";

import { DecisionRow } from "../../types";
import { IncreaseValues } from "./types";

import { useDecisionSubmit, useMyDecisionDetails, useSubmitDecisions } from "../../../../api/hooks";
import {
	type CompensationDecisionNotesLeavePayload,
	useDecisionNotesAutosave,
} from "../../../../hooks/useDecisionNotesAutosave";

import { updateMyDecisionsNotes } from "../../../../api/services/defs/internal-api/compensation";

import ErrorPage from "../../../Error";
import { useCompensationAccess } from "../../useCompensationAccess";
import {
	CurrentSalaryCellRenderer,
	EmployeeInfoCellRenderer,
	EmployeeNameCellRenderer,
	IncreaseAmountCellRenderer,
	IncreasePercentCellRenderer,
	NewSalaryCellRenderer,
	SuggestedIncreaseCellRenderer,
	SuperRowRenderer,
	notesPayloadKey,
	toDecisionRow,
} from "../CellRenderers";

export type { CompensationDecisionNotesLeavePayload };

const myDecisionsColumnDefs: ColDef<DecisionRow>[] = [
	{
		headerName: "Employee",
		field: "employee_name",
		cellRenderer: EmployeeNameCellRenderer,
		width: 200,
		flex: 1.5,
		sortable: true,
	},
	{
		headerName: "Current Salary",
		field: "current_salary",
		cellRenderer: CurrentSalaryCellRenderer,
		flex: 1,
		sortable: true,
	},
	{
		headerName: "Suggested",
		flex: 0.8,
		sortable: false,
		cellRenderer: SuggestedIncreaseCellRenderer,
	},
	{
		headerName: "Increase",
		field: "proposed_increase_percent",
		cellRenderer: IncreasePercentCellRenderer,
		flex: 1,
		sortable: false,
	},
	{
		headerName: "Increase",
		field: "proposed_increase_amount",
		cellRenderer: IncreaseAmountCellRenderer,
		flex: 1,
		sortable: false,
	},
	{
		headerName: "New Salary",
		field: "proposed_salary",
		cellRenderer: NewSalaryCellRenderer,
		flex: 1,
		sortable: true,
	},
	{
		headerName: "Employee Info",
		cellRenderer: EmployeeInfoCellRenderer,
		sortable: false,
		resizable: false,
	},
];

const MyDecisions: React.FC = () => {
	const { cycleId } = useParams<{ cycleId: string }>();
	const {
		userID,
		isLoading: isAccessLoading,
		compensationDisabled,
		canMakeDecisions,
		canLoadMyDecisions,
	} = useCompensationAccess(cycleId);
	const {
		data: myDecisionsData,
		isLoading: isLoadingMyDecisions,
		isError: isMyDecisionsError,
		isPending: isMyDecisionsPending,
	} = useMyDecisionDetails(userID ?? "", cycleId ?? "", {
		enabled: canLoadMyDecisions,
	});
	const hasDecisionsToMake =
		!isLoadingMyDecisions &&
		!isMyDecisionsPending &&
		(myDecisionsData?.directReportsToMakeDecisionsFor?.results?.length ?? 0) > 0;
	const { compensationCycleDetails: cycleDetails, directReportsToMakeDecisionsFor: myDecisionsDetails } =
		myDecisionsData ?? {};
	const [draftOverrides, setDraftOverrides] = useState<IncreaseValues>({});

	const [expandedRowIdsByGroup, setExpandedRowIdsByGroup] = useState<Record<string, string | null>>({});
	const [refreshingGroupIds, setRefreshingGroupIds] = useState<Record<string, boolean>>({});
	const [groupCannotBeSubmitted, setGroupCannotBeSubmitted] = useState<string | null>(null);
	const { mutate: submitDecisions } = useSubmitDecisions();
	const notesValuesRef = useRef<Record<string, string>>({});
	const activeNoteEditorRef = useRef<string | null>(null);
	const gridRefs = useRef<Record<string, TableRef<DecisionRow>>>({});

	let cycleStatus: string = cycleDetails?.status || "";
	if (cycleDetails?.status === "active" && cycleDetails?.startDate > new Date()) {
		cycleStatus = "scheduled";
	}

	const groupedByManagerId = useMemo(() => {
		const results = myDecisionsDetails?.results ?? [];
		type GroupedResults = Record<string, typeof results>;

		const grouped = results.reduce((acc: GroupedResults, user) => {
			const managerId = String(user.managerId ?? "");
			if (!managerId) return acc;

			acc[managerId] ??= [];
			acc[managerId].push(user);
			return acc;
		}, {});

		return Object.entries(grouped as GroupedResults).map(([managerId, users]) => ({
			managerId,
			users,
		}));
	}, [myDecisionsDetails?.results]);

	const employeeIdToManagerId = useMemo(() => {
		const map = new Map<string, string>();
		for (const g of groupedByManagerId) {
			for (const u of g.users) {
				map.set(String(u.employeeId), g.managerId);
			}
		}
		return map;
	}, [groupedByManagerId]);

	const baseDecisionRowsByManagerId = useMemo(
		() =>
			Object.fromEntries(
				groupedByManagerId.map((group) => [
					group.managerId,
					group.users.map((dr, index) => toDecisionRow(dr, index, userID)),
				])
			) as Record<string, DecisionRow[]>,
		[groupedByManagerId, userID]
	);

	const baseDecisionRowsFlat = useMemo(
		() => Object.values(baseDecisionRowsByManagerId).flat(),
		[baseDecisionRowsByManagerId]
	);

	useEffect(() => {
		const next = { ...notesValuesRef.current };
		let changed = false;
		for (const r of baseDecisionRowsFlat) {
			const key = notesPayloadKey(r);
			if (!key) continue;
			const text = r.notes;
			if (text != null && text !== "" && next[key] === undefined) {
				next[key] = text;
				changed = true;
			}
		}
		if (changed) {
			notesValuesRef.current = next;
		}
	}, [baseDecisionRowsFlat]);

	/** Server defaults from grouped rows (same `toDecisionRow` keys as the grid) merged with local draft edits. */
	const increaseValues: IncreaseValues = useMemo(() => {
		const merged: IncreaseValues = {};
		for (const rows of Object.values(baseDecisionRowsByManagerId)) {
			for (const row of rows) {
				if (draftOverrides[row._id]) {
					merged[row._id] = draftOverrides[row._id];
				} else {
					merged[row._id] = {
						percent: row.proposed_increase_percent ?? null,
						amount: row.proposed_increase_amount ?? null,
					};
				}
			}
		}
		return merged;
	}, [draftOverrides, baseDecisionRowsByManagerId]);

	const salaryById = useMemo(() => {
		const map = new Map<string, number>();
		for (const rows of Object.values(baseDecisionRowsByManagerId)) {
			for (const row of rows) {
				map.set(row._id, row.current_salary);
			}
		}
		return map;
	}, [baseDecisionRowsByManagerId]);

	const onPercentChange = useCallback(
		(id: string, percent: number | "") => {
			setDraftOverrides((prev) => {
				if (percent === "") return { ...prev, [id]: { percent: "", amount: "" } };
				const salary = salaryById.get(id) ?? 0;
				const amount = Math.round(salary * (percent / 100) * 100) / 100;
				return { ...prev, [id]: { percent, amount } };
			});
		},
		[salaryById]
	);

	const onAmountChange = useCallback(
		(id: string, amount: number | "") => {
			setDraftOverrides((prev) => {
				if (amount === "") return { ...prev, [id]: { percent: "", amount: "" } };
				const salary = salaryById.get(id) ?? 0;
				const percent = salary > 0 ? Math.round((amount / salary) * 100 * 100) / 100 : 0;
				return { ...prev, [id]: { percent, amount } };
			});
		},
		[salaryById]
	);

	const onExpand = useCallback((groupId: string, rowId: string) => {
		setExpandedRowIdsByGroup((prev) => ({ ...prev, [groupId]: rowId }));
	}, []);

	const onCollapse = useCallback((groupId: string) => {
		setExpandedRowIdsByGroup((prev) => ({ ...prev, [groupId]: null }));
	}, []);

	const { schedulePersist: scheduleNotesAutosave } = useDecisionNotesAutosave({
		notesValuesRef,
		cycleId,
		managerId: userID,
		persist: updateMyDecisionsNotes,
		debounceMs: 800,
	});

	const onNotesChange = useCallback(
		(employeeId: string, value: string) => {
			notesValuesRef.current = { ...notesValuesRef.current, [employeeId]: value };
			scheduleNotesAutosave();

			const managerId = employeeIdToManagerId.get(employeeId);
			const gridsToPatch =
				managerId != null && gridRefs.current[managerId]
					? [gridRefs.current[managerId]]
					: Object.values(gridRefs.current);

			for (const grid of gridsToPatch) {
				const api = grid?.api;
				if (!api) continue;
				api.forEachNode((node) => {
					const d = node.data;
					if (d && !d.superRow && String(d.employee_id) === employeeId) {
						node.setData({ ...d, notes: value });
					}
				});
			}
		},
		[employeeIdToManagerId, scheduleNotesAutosave]
	);

	const groupDecisionRowsByManagerId = useMemo(() => {
		return Object.fromEntries(
			Object.entries(baseDecisionRowsByManagerId).map(([managerId, rows]) => {
				const rowsWithIncreaseValues = rows.map((row) => {
					const draft = increaseValues[row._id];
					const proposedIncreasePercent =
						draft?.percent === "" ? null : (draft?.percent ?? row.proposed_increase_percent ?? null);
					const proposedIncreaseAmount =
						draft?.amount === "" ? null : (draft?.amount ?? row.proposed_increase_amount ?? null);

					return {
						...row,
						proposed_increase_percent: proposedIncreasePercent,
						proposed_increase_amount: proposedIncreaseAmount,
						proposed_salary:
							proposedIncreaseAmount != null
								? row.current_salary + proposedIncreaseAmount
								: row.proposed_salary,
						notes: notesValuesRef.current[row.employee_id] ?? row.notes ?? "",
					};
				});

				return [managerId, rowsWithIncreaseValues];
			})
		) as Record<string, DecisionRow[]>;
	}, [baseDecisionRowsByManagerId, increaseValues]);

	useEffect(() => {
		if (groupCannotBeSubmitted == null) {
			return;
		}
		const rows = groupDecisionRowsByManagerId[groupCannotBeSubmitted] ?? [];
		const hasNullIncreases = rows.some(
			(row) => row.proposed_increase_amount === null || row.proposed_increase_percent === null
		);
		if (!hasNullIncreases) {
			setGroupCannotBeSubmitted(null);
		}
	}, [groupCannotBeSubmitted, groupDecisionRowsByManagerId]);

	const handleSubmitDecisions = useDecisionSubmit({
		cycleId,
		userID,
		groupDecisionRowsByManagerId,
		submitDecisions,
		setGroupCannotBeSubmitted,
		setRefreshingGroupIds,
		setDraftOverrides,
	});

	const validateSubmitDecisions = useCallback(
		(managerId: string) => {
			if (!managerId) {
				return;
			}
			const groupRows = groupDecisionRowsByManagerId[managerId] ?? [];
			if (!groupRows.length || !userID || !cycleId) return;
			const rowsWithNullIncreases = groupRows.filter(
				(row) => row.proposed_increase_amount === null || row.proposed_increase_percent === null
			);
			if (rowsWithNullIncreases.length) {
				setGroupCannotBeSubmitted(managerId);
				return;
			}
			setGroupCannotBeSubmitted(null);
			handleSubmitDecisions(managerId);
		},
		[cycleId, groupDecisionRowsByManagerId, handleSubmitDecisions, userID]
	);

	const rawAllotment = cycleDetails?.allotmentIncreasePercent;
	const allotmentPercent = typeof rawAllotment === "number" && Number.isFinite(rawAllotment) ? rawAllotment : 0;
	const recommendedIncreasePercent = allotmentPercent;
	const baseGridContext = useMemo(
		() => ({
			increaseValues,
			recommendedIncreasePercent,
			onPercentChange,
			onAmountChange,
			notesValuesRef,
			onNotesChange,
			groupCannotBeSubmitted: groupCannotBeSubmitted ?? null,
			activeNoteEditorRef,
		}),
		[
			increaseValues,
			recommendedIncreasePercent,
			onPercentChange,
			onAmountChange,
			onNotesChange,
			groupCannotBeSubmitted,
		]
	);

	// Refresh cells when draft values, validation flag, or expansion change
	// (AG Grid can keep stale `context` on React renderers until refresh).
	useEffect(() => {
		Object.values(gridRefs.current).forEach((grid) => {
			const api = grid?.api;
			if (!api) return;
			api.refreshCells({ force: true });
		});
	}, [increaseValues, groupCannotBeSubmitted, expandedRowIdsByGroup]);

	// Fast permission/feature-flag checks first — no need to wait for slow data fetches
	if (isAccessLoading) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (compensationDisabled || !canMakeDecisions) {
		return <ErrorPage code={403}></ErrorPage>;
	}

	// Data-dependent checks — only reached after permissions are confirmed
	if (canLoadMyDecisions && isMyDecisionsPending) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (canLoadMyDecisions && isMyDecisionsError && myDecisionsData == null) {
		return <ErrorPage code={404} />;
	}

	if (cycleStatus !== "active") {
		return <ErrorPage code={403}></ErrorPage>;
	}

	if (!hasDecisionsToMake) {
		return <ErrorPage code={403}></ErrorPage>;
	}
	return (
		<MyDecisionsGroups
			groupedByManagerId={groupedByManagerId}
			refreshingGroupIds={refreshingGroupIds}
			userID={userID}
			expandedRowIdsByGroup={expandedRowIdsByGroup}
			baseGridContext={baseGridContext}
			onExpand={onExpand}
			onCollapse={onCollapse}
			recommendedIncreasePercent={recommendedIncreasePercent}
			increaseValues={increaseValues}
			handleSubmitDecisions={validateSubmitDecisions}
			gridRefs={gridRefs}
			columnDefs={myDecisionsColumnDefs}
			notesPayloadKey={notesPayloadKey}
			fullWidthCellRenderer={SuperRowRenderer}
			groupCannotBeSubmitted={groupCannotBeSubmitted}
			cycleStatus={cycleStatus}
		/>
	);
};

export default MyDecisions;
